import { z } from 'zod';
import { LEVELS, ROLES, CATEGORIES, DEFINITION_KINDS, define } from '../types.js';

/**
 * Rideshare provider enum.
 */
export const RideshareProviderSchema = z.enum(['uber', 'lyft']);

export type RideshareProvider = z.infer<typeof RideshareProviderSchema>;

/**
 * Rideshare orchestration state.
 *
 * @remarks
 * System-managed object storing full rideshare booking state. Set by hoprides
 * service during ride estimation and booking. Includes:
 * - Estimate timing and refresh schedule
 * - Selected provider and product
 * - ETA calculations
 * - Ride-after timing fields for delayed booking
 */
export const schema = z
  .object({
    last_estimate_at: z.string().describe('ISO timestamp of last estimate fetch').optional(),
    next_estimate_at: z.string().describe('ISO timestamp for next scheduled estimate refresh').optional(),
    rideshare_eta_seconds: z.number().describe('Estimated time of arrival in seconds').optional(),
    selected_provider: RideshareProviderSchema.describe('Selected rideshare provider (uber or lyft)').optional(),
    selected_product: z.string().describe('Selected product/tier (e.g., UberX, Lyft XL)').optional(),
    target_pickup_time: z.string().describe('Target pickup time for ride-after scenarios (ISO timestamp)').optional(),
    best_time_to_call: z.string().describe('Optimal time to request the ride (ISO timestamp)').optional(),
    booking_lead_time_seconds: z.number().describe('Lead time needed before target pickup').optional(),
    calculation_method: z.string().describe('Method used to calculate timing (e.g., "eta-based", "fixed")').optional(),
  })
  .strict()
  .optional();

export type RideOrchestration = z.infer<typeof schema>;

export const definition = define({
  key: 'ride_orchestration',
  schema,
  label: 'Ride Orchestration',
  description:
    'Rideshare booking state including estimates, provider selection, and timing calculations. Set by hoprides service, not user-configurable.',
  kind: DEFINITION_KINDS.OUTPUT,
  category: CATEGORIES.OPERATIONAL_STATE.key,
  writableAtLevels: [LEVELS.MOVE],
  readableByRoles: [ROLES.ADMIN],
});
